import { useEffect, useRef } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { useApiAuth } from '@/hooks/use-api-auth';
import { apiClient } from '@/lib/api-client';

function parseJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64)) as Record<string, unknown>;
}

export function OrgEnrollmentTracker() {
  const { user, isLoading, getAccessToken, signIn } = useAuth();
  const { isAuthReady } = useApiAuth();
  const enrolling = useRef(false);

  useEffect(() => {
    if (!isAuthReady || isLoading || !user || enrolling.current) return;

    enrolling.current = true;

    (async () => {
      // Decode the current token to check for a role claim.
      // `role` is only present when the user has formal org membership — org_id
      // alone (from org-scoped signIn) is not sufficient.
      let hasRole = false;
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          const token = await getAccessToken();
          if (token) {
            hasRole = !!parseJwtPayload(token).role;
            break;
          }
        } catch {
          if (attempt < 4) await new Promise((r) => setTimeout(r, 200 * (attempt + 1)));
        }
      }

      if (hasRole) {
        enrolling.current = false;
        return;
      }

      try {
        await apiClient.post('/users/me/ensure-org');
        await signIn({
          organizationId: import.meta.env.VITE_WORKOS_DEFAULT_ORG_ID,
          state: window.location.pathname + window.location.search,
        });
      } catch {
        enrolling.current = false;
      }
    })();
  }, [isAuthReady, isLoading, user, getAccessToken, signIn]);

  return null;
}
